import { useState, useEffect, useRef, RefObject } from 'react';
import { useFormik } from 'formik';

import type {
  IAssistantScript,
  TriggerType,
  TriggerInterruption,
} from '@shared/interfaces/assistantScript';
import { PlaybackPolicy } from '@shared/interfaces/assistantScript';
import type {
  ICreateAssistantScript,
  IUpdateAssistantScript,
  ScriptStep,
  UserInputMode,
} from '@shared/interfaces/assistantScripts';
import { PollyLanguage } from '@shared/interfaces/polly';
import { msToSeconds, secondsToMs, getUserInputMode } from '../utils';

interface UseScriptStepsParams {
  assistantScript?: IAssistantScript | null;
  trigger?: string;
  defaultTriggerType?: TriggerType;
  title?: string;
  mapName?: string;
  brandingId?: string;
  poiId?: string;
  scriptStepsEndRef: RefObject<HTMLDivElement | null>;
  onSave: (
    values: IUpdateAssistantScript | ICreateAssistantScript,
  ) => Promise<void>;
  onTranslate: (
    langs: PollyLanguage[],
    texts: string[],
  ) => Promise<Partial<Record<PollyLanguage, string[]>>>;
}

const useScriptSteps = ({
  assistantScript,
  trigger,
  defaultTriggerType,
  title,
  mapName,
  brandingId,
  poiId,
  scriptStepsEndRef,
  onSave,
  onTranslate,
}: UseScriptStepsParams) => {
  const defaultStep: ScriptStep = {
    text: '',
    translations: {},
    allowedKeys: [],
    delayBeforeAdvancing: 0,
    advanceOnAudioEnd: false,
    awaitButtonNext: false,
    enableClickAnywhere: false,
    questionary: { type: 'none' },
  };

  const [scriptSteps, setScriptSteps] = useState<ScriptStep[]>(() =>
    assistantScript ? [] : [defaultStep],
  );
  const [stepExpanded, setStepExpanded] = useState<boolean[]>(() =>
    assistantScript ? [] : [true],
  );
  const [stepUserInputModes, setStepUserInputModes] = useState<UserInputMode[]>(
    () => (assistantScript ? [] : ['none']),
  );
  const [stepsModified, setStepsModified] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const initialTriggerType: TriggerType =
    defaultTriggerType ?? (poiId ? 'object' : 'ui_click');

  const [triggerType, setTriggerType] = useState<TriggerType>(initialTriggerType);
  const [uiClickButtonLabel, setUiClickButtonLabelRaw] = useState('Take a tour');
  const [uiClickButtonLabelTranslations, setUiClickButtonLabelTranslations] =
    useState<Partial<Record<PollyLanguage, string>>>({});
  const [uiClickButtonLabelStale, setUiClickButtonLabelStale] = useState<
    Partial<Record<PollyLanguage, boolean>>
  >({});
  const [interruptMode, setInterruptMode] =
    useState<TriggerInterruption>('finish_step');
  const [bridgeRegionGaps, setBridgeRegionGaps] = useState(false);
  const [allowSkipDialog, setAllowSkipDialog] = useState(true);
  const [playbackPolicy, setPlaybackPolicy] = useState<PlaybackPolicy>(
    initialTriggerType === 'ui_click' || initialTriggerType === 'custom_spot'
      ? PlaybackPolicy.ALWAYS
      : PlaybackPolicy.ONCE_SESSION,
  );
  const [skipFirstSession, setSkipFirstSession] = useState(false);

  // Stable ref so scrollToBottom inside addNewStep always sees the current ref
  const stepsEndRef = useRef(scriptStepsEndRef);
  stepsEndRef.current = scriptStepsEndRef;

  useEffect(() => {
    if (!assistantScript) {
      return;
    }

    const scriptData = JSON.parse(assistantScript.scriptData);

    setStepExpanded(scriptData.map(() => true));
    setStepUserInputModes(scriptData.map(getUserInputMode));
    setScriptSteps(msToSeconds(scriptData));

    setTriggerType(
      assistantScript.triggerType ? assistantScript.triggerType : 'area_entry',
    );

    if (assistantScript.interruptMode) {
      setInterruptMode(assistantScript.interruptMode);
    }

    if (assistantScript.bridgeRegionGaps !== undefined) {
      setBridgeRegionGaps(assistantScript.bridgeRegionGaps);
    }

    setAllowSkipDialog(
      assistantScript.canUserSkip !== undefined
        ? assistantScript.canUserSkip
        : false,
    );

    if (assistantScript.playbackPolicy) {
      setPlaybackPolicy(assistantScript.playbackPolicy);
    }

    if (assistantScript.skipFirstSession !== undefined) {
      setSkipFirstSession(assistantScript.skipFirstSession);
    }

    setUiClickButtonLabelRaw(
      assistantScript.uiClickButtonLabel ?? 'Take a tour',
    );
    setUiClickButtonLabelTranslations(
      assistantScript.uiClickButtonLabelTranslations ?? {},
    );
  }, []);

  // PlaybackPolicySection only ever renders a single "Always" card for
  // ui_click (see its own filter) — the initial playbackPolicy state above
  // only matches that on first mount. Without this, switching triggerType to
  // ui_click afterward (trigger step, or "Edit trigger" in EditSummary)
  // leaves a stale non-ALWAYS value in state: the single card then shows
  // unselected, and an untouched policy step would silently save that stale
  // value instead of ALWAYS.
  useEffect(() => {
    if (triggerType === 'ui_click') {
      setPlaybackPolicy(PlaybackPolicy.ALWAYS);
    }
  }, [triggerType]);

  const getInitialScriptTrigger = () => {
    if (poiId) {
      return [poiId];
    }

    if (assistantScript?.scriptId) {
      const t = Array.isArray(assistantScript.scriptTrigger)
        ? assistantScript.scriptTrigger
        : [assistantScript.scriptTrigger];

      return (t as string[]).filter(Boolean);
    }

    if (trigger) {
      return [trigger];
    }

    return [];
  };

  const onSubmit = async ({
    scriptTrigger: formikTrigger,
  }: Pick<IAssistantScript, 'scriptTrigger'>) => {
    const scriptTrigger = poiId ? [poiId] : formikTrigger;
    const formatScriptData = JSON.stringify(secondsToMs(scriptSteps));

    // Button-driven scripts (ui_click / custom_spot) are always
    // non_interruptible — the user starts them explicitly via the button, so
    // the interruption policy is hidden in the UI and forced here regardless of
    // the (stale) state value.
    const isButtonDrivenTrigger =
      triggerType === 'ui_click' || triggerType === 'custom_spot';
    const effectiveInterruptMode: TriggerInterruption = isButtonDrivenTrigger
      ? 'non_interruptible'
      : interruptMode;

    setIsSubmitting(true);

    try {
      if (!assistantScript?.scriptId) {
        await onSave({
          scriptTrigger,
          scriptData: formatScriptData,
          title,
          mapName,
          brandingId,
          triggerType,
          interruptMode: effectiveInterruptMode,
          bridgeRegionGaps,
          canUserSkip: allowSkipDialog,
          uiClickButtonLabel,
          uiClickButtonLabelTranslations,
          playbackPolicy,
          skipFirstSession,
        } as ICreateAssistantScript);
      } else {
        await onSave({
          scriptTrigger,
          scriptData: formatScriptData,
          title: title ?? assistantScript?.title,
          mapName,
          triggerType,
          interruptMode: effectiveInterruptMode,
          bridgeRegionGaps,
          canUserSkip: allowSkipDialog,
          uiClickButtonLabel,
          uiClickButtonLabelTranslations,
          playbackPolicy,
          skipFirstSession,
        } as IUpdateAssistantScript);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      scriptTrigger: getInitialScriptTrigger(),
    },
    onSubmit,
  });

  const addNewStep = () => {
    setScriptSteps((prev) => [
      ...prev,
      {
        text: '',
        translations: {},
        allowedKeys: [],
        delayBeforeAdvancing: 0,
        advanceOnAudioEnd: false,
        awaitButtonNext: false,
        enableClickAnywhere: false,
        questionary: { type: 'none' },
      },
    ]);
    setStepExpanded((prev) => [...prev, true]);
    setStepUserInputModes((prev) => [...prev, 'none']);
    setStepsModified(true);

    if (scriptSteps.length) {
      setTimeout(() => {
        stepsEndRef.current.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  const removeStep = (index: number) => {
    setScriptSteps((prev) => {
      const next = [...prev];
      next.splice(index, 1);
      return next;
    });
    setStepExpanded((prev) => {
      const next = [...prev];
      next.splice(index, 1);
      return next;
    });
    setStepUserInputModes((prev) => {
      const next = [...prev];
      next.splice(index, 1);
      return next;
    });
    setStepsModified(true);
  };

  const updateStep = (index: number, data: Partial<ScriptStep>) => {
    setScriptSteps((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...data };
      return next;
    });
    setStepsModified(true);
  };

  const updateStepTranslation = (
    index: number,
    lang: PollyLanguage,
    value: string,
  ) => {
    setScriptSteps((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        translations: { ...next[index].translations, [lang]: value },
      };
      return next;
    });
    setStepsModified(true);
  };

  // Editing the base label invalidates every existing translation, since
  // they were generated from (and reflect) the previous base text.
  const setUiClickButtonLabel = (value: string) => {
    setUiClickButtonLabelRaw(value);
    setUiClickButtonLabelStale((prev) => {
      const next = { ...prev };

      for (const lang of Object.keys(
        uiClickButtonLabelTranslations,
      ) as PollyLanguage[]) {
        if ((uiClickButtonLabelTranslations[lang] ?? '').trim()) {
          next[lang] = true;
        }
      }
      return next;
    });
    setStepsModified(true);
  };

  const updateUiClickButtonLabelTranslation = (
    lang: PollyLanguage,
    value: string,
  ) => {
    setUiClickButtonLabelTranslations((prev) => ({ ...prev, [lang]: value }));
    setUiClickButtonLabelStale((prev) => ({ ...prev, [lang]: false }));
    setStepsModified(true);
  };

  const moveStepUp = (index: number) => {
    if (index === 0) {
      return;
    }

    setScriptSteps((prev) => {
      const next = [...prev];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next;
    });
    setStepExpanded((prev) => {
      const next = [...prev];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next;
    });
    setStepUserInputModes((prev) => {
      const next = [...prev];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next;
    });
    setStepsModified(true);
  };

  const moveStepDown = (index: number) => {
    if (index === scriptSteps.length - 1) {
      return;
    }

    setScriptSteps((prev) => {
      const next = [...prev];
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      return next;
    });
    setStepExpanded((prev) => {
      const next = [...prev];
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      return next;
    });
    setStepUserInputModes((prev) => {
      const next = [...prev];
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      return next;
    });
    setStepsModified(true);
  };

  const toggleStepExpanded = (index: number) => {
    setStepExpanded((prev) => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  };

  const handleUserInputModeChange = (index: number, mode: UserInputMode) => {
    setStepUserInputModes((prev) => {
      const next = [...prev];
      next[index] = mode;
      return next;
    });

    switch (mode) {
      case 'button_next':
        updateStep(index, {
          awaitButtonNext: true,
          enableClickAnywhere: false,
          allowedKeys: [],
        });
        break;
      case 'click_anywhere':
        updateStep(index, {
          awaitButtonNext: false,
          enableClickAnywhere: true,
          allowedKeys: [],
        });
        break;
      case 'keyboard':
        updateStep(index, {
          awaitButtonNext: false,
          enableClickAnywhere: false,
        });
        break;
      default:
        updateStep(index, {
          awaitButtonNext: false,
          enableClickAnywhere: false,
          allowedKeys: [],
        });
    }
  };

  // Translates the script step texts only (used by the script-steps language
  // tabs). The "Take a tour" button label is handled separately by
  // handleTranslateButtonLabelTo so each control stays scoped to its content.
  const handleTranslateTo = async (langs: PollyLanguage[]) => {
    if (!langs.length) {
      return;
    }

    const texts = scriptSteps.map((s) => s.text);

    if (!texts.some((t) => t.trim())) {
      return;
    }

    setIsTranslating(true);
    try {
      const translations = await onTranslate(langs, texts);

      setScriptSteps((prev) =>
        prev.map((step, i) => {
          const updatedTranslations = { ...step.translations };

          for (const lang of langs) {
            const translated = translations[lang]?.[i];

            if (translated !== undefined) {
              updatedTranslations[lang] = translated;
            }
          }
          return { ...step, translations: updatedTranslations };
        }),
      );
      setStepsModified(true);
    } finally {
      setIsTranslating(false);
    }
  };

  // Translates only the "Take a tour" button label (used by the ui_click
  // button language tabs).
  const handleTranslateButtonLabelTo = async (langs: PollyLanguage[]) => {
    if (!langs.length || !uiClickButtonLabel.trim()) {
      return;
    }

    setIsTranslating(true);
    try {
      const translations = await onTranslate(langs, [uiClickButtonLabel]);

      setUiClickButtonLabelTranslations((prev) => {
        const next = { ...prev };

        for (const lang of langs) {
          const translated = translations[lang]?.[0];

          if (translated !== undefined) {
            next[lang] = translated;
          }
        }
        return next;
      });
      setUiClickButtonLabelStale((prev) => {
        const next = { ...prev };

        for (const lang of langs) {
          if (translations[lang]?.[0] !== undefined) {
            next[lang] = false;
          }
        }
        return next;
      });
      setStepsModified(true);
    } finally {
      setIsTranslating(false);
    }
  };

  return {
    scriptSteps,
    stepExpanded,
    stepUserInputModes,
    stepsModified,
    isSubmitting,
    isTranslating,
    triggerType,
    setTriggerType,
    uiClickButtonLabel,
    setUiClickButtonLabel,
    uiClickButtonLabelTranslations,
    uiClickButtonLabelStale,
    updateUiClickButtonLabelTranslation,
    interruptMode,
    setInterruptMode,
    bridgeRegionGaps,
    setBridgeRegionGaps,
    allowSkipDialog,
    setAllowSkipDialog,
    playbackPolicy,
    setPlaybackPolicy,
    skipFirstSession,
    setSkipFirstSession,
    formik,
    addNewStep,
    removeStep,
    updateStep,
    updateStepTranslation,
    moveStepUp,
    moveStepDown,
    toggleStepExpanded,
    handleUserInputModeChange,
    handleTranslateTo,
    handleTranslateButtonLabelTo,
  };
};

export default useScriptSteps;
